import { EpisodeOfCare, PlanDefinition, Practitioner } from "@medplum/fhirtypes";
import { BillTo, BillToOptions, LabOrganization, TestCoding } from "@medplum/health-gorilla-core";

import { startOfDay } from "date-fns";
import { z } from "zod";

import { coerceDateInput, REQUIRED_FIELD_MESSAGE, zCodingOptional, zResource } from "@/utils/zod";

export const REFERENCE_RANGE_FORMAT_MESSAGE =
  "Reference range must use the format min-max (e.g. 4.5-11)";

/** Empty/whitespace is allowed; non-empty must be exactly "left-right" with one hyphen. */
export function isValidReferenceRangeText(value: string | null | undefined): boolean {
  if (value == null || value.trim() === "") return true;

  const parts = value.trim().split("-");
  return parts.length === 2 && parts[0].length > 0 && parts[1].length > 0;
}

interface ManualLabResultsNode {
  [key: string]: ManualLabResultsValue;
}

type ManualLabResultsValue = string | number | boolean | null | undefined | ManualLabResultsNode;

function addReferenceRangeIssues(
  value: ManualLabResultsValue | undefined,
  ctx: z.RefinementCtx,
  path: (string | number)[],
): void {
  if (value == null || typeof value !== "object" || Array.isArray(value)) return;

  for (const [key, child] of Object.entries(value)) {
    const childPath = [...path, key];

    if (key.endsWith("-reference") && typeof child === "string") {
      if (!isValidReferenceRangeText(child)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REFERENCE_RANGE_FORMAT_MESSAGE,
          path: childPath,
        });
      }
      continue;
    }

    addReferenceRangeIssues(child, ctx, childPath);
  }
}

export const MANUAL_RESULTS_DATE_ORDER_MESSAGE = "Date received must be on or after date ordered";

/**
 * Returns whether ordered/received dates respect expected lab timeline (ordered first).
 * Missing dates are treated as valid here; required-field checks run separately.
 */
function isManualResultsDateOrderValid(
  dateOrdered: Date | undefined,
  dateReceived: Date | undefined,
): boolean {
  if (!dateOrdered || !dateReceived) {
    return true;
  }

  return startOfDay(dateOrdered) <= startOfDay(dateReceived);
}

// TODO[LABS]: Add validation to ensure requestor has NPI set on their identifier field.
export const AddLabOrderFormSchema = z.object({
  performingLab: zResource<LabOrganization>("Organization"),
  template: zResource<PlanDefinition>("PlanDefinition").optional(),
  labTests: z
    .record(z.string(), z.custom<TestCoding>())
    .refine((tests) => Object.keys(tests).length > 0, {
      message: "At least one lab test is required",
    }),
  requestor: zResource<Practitioner>("Practitioner"),
  episodeOfCare: zResource<EpisodeOfCare>("EpisodeOfCare").optional(),
  diagnoses: z.array(zCodingOptional()),
  billTo: z.enum(BillToOptions, { message: "This field is required" }),
  selectedCoverages: z.array(z.string()).default([]),
  notes: z.string().default(""),
  specimenCollectionTime: z.date().optional(),
  encounterId: z.string().optional(),
});

export interface AddLabOrderFormType
  extends Omit<
    z.infer<typeof AddLabOrderFormSchema>,
    "performingLab" | "template" | "requestor" | "episodeOfCare" | "billTo"
  > {
  performingLab: LabOrganization | undefined;
  template: PlanDefinition | undefined;
  requestor: Practitioner | undefined;
  episodeOfCare: EpisodeOfCare | undefined;
  billTo: BillTo | undefined;
}

export const AddManualResultsFormSchema = z
  .object({
    testsOrPanels: z
      .array(z.string())
      .min(1, { message: "At least one test or panel is required" }),
    requester: zResource<Practitioner>("Practitioner").optional(),
    episodeOfCare: zResource<EpisodeOfCare>("EpisodeOfCare").optional(),
    dateOrdered: z.preprocess(coerceDateInput, z.date({ message: REQUIRED_FIELD_MESSAGE })),
    dateReceived: z.preprocess(coerceDateInput, z.date({ message: REQUIRED_FIELD_MESSAGE })),

    results: z.record(z.any()).optional(),
  })
  .superRefine((data, ctx) => {
    if (!isManualResultsDateOrderValid(data.dateOrdered, data.dateReceived)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: MANUAL_RESULTS_DATE_ORDER_MESSAGE,
        path: ["dateReceived"],
      });
    }

    if (data.results) {
      addReferenceRangeIssues(data.results, ctx, ["results"]);
    }
  });

export interface AddManualResultsFormType
  extends Omit<
    z.infer<typeof AddManualResultsFormSchema>,
    "requester" | "episodeOfCare" | "dateOrdered" | "dateReceived"
  > {
  /**
   * Mantine v8 Path<T> recurses through Practitioner → extension → Extension → valueCoding →
   * Coding → extension → ... causing tsc to hang. Pick with only scalar fields breaks the cycle;
   * runtime values are still full Practitioner objects (structurally compatible).
   */
  requester: Pick<Practitioner, "resourceType" | "id"> | undefined;
  episodeOfCare: Pick<EpisodeOfCare, "resourceType" | "id"> | undefined;
  dateOrdered: Date | undefined;
  dateReceived: Date | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  results?: Record<string, any>;
}

/**
 * Parses raw form values through the schema (coercing DateInput strings to Date) and returns the
 * form type used by services. Cast is required because z.infer marks optional fields with `?`
 * while AddManualResultsFormType uses explicit `| undefined` for Mantine compatibility.
 */
export function parseAddManualResultsForm(
  values: AddManualResultsFormType,
): AddManualResultsFormType {
  return AddManualResultsFormSchema.parse(values) as AddManualResultsFormType;
}
